import clsx from 'clsx';
import {
  ButtonHTMLAttributes,
  ForwardedRef,
  forwardRef,
  MouseEvent,
} from 'react';
import Link from 'next/link';
import { overrideTailwindClasses } from 'tailwind-override';

import { ButtonModes } from 'constants/ButtonModes';
import { externalLinkMixin } from 'utils/externalLinkMixin';

import Icon, { IconSizeType, IconType } from './Icon';

export interface ButtonProps
  extends Omit<
    ButtonHTMLAttributes<HTMLButtonElement | HTMLAnchorElement>,
    'disabled'
  > {
  size?: ButtonSize;
  mode?: ButtonModes;
  isDisabled?: boolean;
  icon?: IconType;
  iconFill?: string;
  iconPosition?: 'left' | 'right';
  iconSize?: IconSizeType;
  fill?: string;
  href?: string;
  rel?: string;
  shallow?: boolean;
  isWide?: boolean;
  isLoading?: boolean;
  scroll?: boolean;
}

type ButtonSize = 'full' | 'small' | 'xsmall' | 'full-icon';

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    {
      size = 'full',
      children,
      className,
      isDisabled,
      type = 'button',
      mode = ButtonModes.primary,
      iconPosition = 'left',
      iconSize = 'xl',
      icon,
      href,
      rel,
      shallow,
      iconFill,
      isWide = false,
      scroll,
      ...rest
    },
    ref
  ): JSX.Element => {
    const classes = overrideTailwindClasses(
      clsx('btn relative', {
        'h-[56px] px-9': size === 'full',
        'text-[13px] px-4 py-2 font-medium leading-5': size === 'small',
        'h-[24px] px-4 min-w-0': size === 'xsmall',
        'h-10 w-10 p-0 !min-w-0': size === 'full-icon',
        'btn-flat': mode === ButtonModes.flat,
        'btn-flat white': mode === ButtonModes.flatWhite,
        'btn-flat black': mode === ButtonModes.flatBlack,
        'btn-primary': mode === ButtonModes.primary,
        'btn-secondary': mode === ButtonModes.secondary,
        'btn-secondary-black': mode === ButtonModes.secondaryBlack,
        'btn-secondary-white': mode === ButtonModes.secondaryWhite,
        'btn-secondary-beige': mode === ButtonModes.secondaryBeige,
        'btn-secondary btn-secondary-warmgray-2':
          mode === ButtonModes.secondaryWarmGray2,
        'btn-menu-link': mode === ButtonModes.menuLink,
        'rounded-full': true,
        noDecorate: href,
        'w-auto w-[240px]': isWide,
        [className || '']: className,
      })
    );

    const buttonChildren = (
      <>
        {icon && iconPosition === 'left' && (
          <Icon
            data-testid="button-icon"
            icon={icon}
            fill={iconFill}
            size={iconSize}
          />
        )}
        {children}
        {icon && iconPosition === 'right' && (
          <Icon
            data-testid="button-icon"
            icon={icon}
            fill={iconFill}
            size={iconSize}
          />
        )}
      </>
    );

    if (href && !isDisabled) {
      const { 'aria-label': ariaLabel, onClick } = rest;
      const handleClick =
        onClick !== undefined
          ? (event: MouseEvent<HTMLAnchorElement>) => {
              const proceed = onClick(event);
              if (proceed !== undefined) {
                return proceed;
              }
              return true;
            }
          : undefined;
      if (href.startsWith('#')) {
        return (
          <a
            onClick={handleClick}
            data-testid="button"
            className={classes}
            ref={ref as ForwardedRef<HTMLAnchorElement>}
            aria-label={ariaLabel}
            href={href}
          >
            {buttonChildren}
          </a>
        );
      }
      return (
        <Link
          shallow={shallow}
          href={href}
          prefetch={false}
          scroll={scroll}
          legacyBehavior
        >
          <a
            onClick={handleClick}
            data-testid="button"
            className={classes}
            ref={ref as ForwardedRef<HTMLAnchorElement>}
            aria-label={ariaLabel}
            {...externalLinkMixin(href)}
          >
            {buttonChildren}
          </a>
        </Link>
      );
    }

    return (
      <button
        data-testid="button"
        className={classes}
        ref={ref as ForwardedRef<HTMLButtonElement>}
        type={type}
        disabled={isDisabled}
        {...rest}
      >
        {buttonChildren}
      </button>
    );
  }
);

Button.displayName = 'Button';
export default Button;
